import React from 'react';
import { motion } from 'framer-motion';
import { 
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CogIcon,
  ServerIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const DBTalkerPage: React.FC = () => {
  const features = [
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Natural Language Queries',
      description: 'Ask questions in plain English, get instant answers from your database without writing SQL.'
    },
    {
      icon: ChartBarIcon,
      title: 'Visual Insights',
      description: 'Automatic chart generation - see trends, patterns, and KPIs visualized instantly.'
    },
    {
      icon: CogIcon,
      title: 'White-Label Branding',
      description: 'Fully customizable interface with your branding, colors, and logo.'
    },
    {
      icon: ServerIcon,
      title: 'Universal Database Support',
      description: 'Connect to any SQL database, data warehouse, or analytics platform via MCP.'
    }
  ];

  const useCases = [
    {
      role: 'CEO',
      question: '"What were our top 5 products by revenue last quarter?"',
      result: 'Instant chart showing product performance with revenue breakdown'
    },
    {
      role: 'Sales Director',
      question: '"Show me conversion rates by region this month"',
      result: 'Geographic heatmap with conversion metrics and trends'
    },
    {
      role: 'CFO',
      question: '"Compare our expenses vs budget across departments"',
      result: 'Bar chart showing budget variance analysis by department'
    },
    {
      role: 'Operations Manager',
      question: '"What\'s our inventory turnover rate?"',
      result: 'Real-time metrics with historical comparison charts'
    }
  ];

  const benefits = [
    'No SQL knowledge required',
    'Mobile-responsive for on-the-go insights',
    'Auto-discovers database schemas and relationships',
    'Secure, role-based data access',
    'Deploy in days, not months',
    'Flexible licensing tailored to your needs'
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <LightBulbIcon className="w-5 h-5" />
              <span className="text-sm font-semibold">New: Business Intelligence Reimagined</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              DB Talker
            </h1>
            <p className="text-2xl md:text-3xl mb-4 text-blue-100">
              Chat With Your Data. Get Instant Answers.
            </p>
            <p className="text-xl mb-12 max-w-3xl mx-auto text-blue-50">
              Transform your database into an intelligent assistant. Ask questions in plain English, 
              get executive-ready answers with charts and insights in seconds.
            </p>
            <div className="flex flex-col items-center">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/schedule-meeting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center gap-2 text-lg shadow-xl"
                >
                  Try Live Demo
                  <ArrowRightIcon className="w-5 h-5" />
                </a>
                <a
                  href="#architecture"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200 text-lg"
                >
                  See How It Works
                </a>
              </div>
              <p className="text-sm text-indigo-100 mt-4">
                Experience DB Talker's natural language database queries
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Business Intelligence Problem
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-red-50 p-8 rounded-2xl border-l-4 border-red-500"
            >
              <h3 className="text-2xl font-bold text-red-600 mb-4">❌ Traditional BI Tools</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span>Require SQL knowledge or data analysts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span>Days or weeks to get simple answers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span>Expensive per-user licensing models</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span>Complex dashboards that become outdated</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span>Not mobile-friendly for executives on the go</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-green-50 p-8 rounded-2xl border-l-4 border-green-500"
            >
              <h3 className="text-2xl font-bold text-green-600 mb-4">✅ DB Talker</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span>Ask questions in plain English - anyone can use it</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span>Get answers instantly with auto-generated charts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span>Flexible licensing - tailored to your needs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span>Dynamic insights based on current data</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span>Mobile-first design for anywhere access</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section id="architecture" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How DB Talker Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A revolutionary 3-layer architecture powered by Model Context Protocol (MCP)
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="space-y-8">
              {/* Layer 1: Chat Interface */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="border-2 border-blue-200 rounded-xl p-6 bg-blue-50"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">1</div>
                  <h3 className="text-2xl font-bold text-gray-900">White-Label Chat Interface</h3>
                </div>
                <p className="text-gray-700 ml-16">
                  Mobile-responsive chat UI with automatic chart generation, customizable branding, 
                  and intuitive conversation flow. Users ask questions naturally, get visual answers instantly.
                </p>
              </motion.div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-8 h-8 text-gray-400 transform rotate-90" />
              </div>

              {/* Layer 2: Configuration Engine */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="border-2 border-purple-200 rounded-xl p-6 bg-purple-50"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-purple-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">2</div>
                  <h3 className="text-2xl font-bold text-gray-900">Configuration Engine (JSON-based)</h3>
                </div>
                <p className="text-gray-700 ml-16 mb-3">
                  Simple JSON configuration for complete customization without code changes:
                </p>
                <ul className="text-gray-700 ml-16 space-y-2">
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2" />
                    <span><strong>Branding:</strong> Logo, colors, company name</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2" />
                    <span><strong>Data Sources:</strong> Database connections and credentials</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2" />
                    <span><strong>Prompts:</strong> Custom AI behavior and business context</span>
                  </li>
                </ul>
              </motion.div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-8 h-8 text-gray-400 transform rotate-90" />
              </div>

              {/* Layer 3: MCP Adapter Layer */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="border-2 border-indigo-200 rounded-xl p-6 bg-indigo-50"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-indigo-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">3</div>
                  <h3 className="text-2xl font-bold text-gray-900">Universal MCP Adapter Layer</h3>
                </div>
                <p className="text-gray-700 ml-16 mb-3">
                  Intelligent middleware that automatically discovers database schemas, relationships, 
                  and generates optimized queries:
                </p>
                <ul className="text-gray-700 ml-16 space-y-2">
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2" />
                    <span>Auto-discovers tables, columns, and foreign keys</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2" />
                    <span>Understands business relationships and context</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2" />
                    <span>Generates and executes SQL automatically</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2" />
                    <span>Formats results for visualization</span>
                  </li>
                </ul>
              </motion.div>

              <div className="flex justify-center">
                <ArrowRightIcon className="w-8 h-8 text-gray-400 transform rotate-90" />
              </div>

              {/* Layer 4: MCP Servers */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="border-2 border-green-200 rounded-xl p-6 bg-green-50"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <ServerIcon className="w-12 h-12 text-green-600" />
                  <h3 className="text-2xl font-bold text-gray-900">MCP Servers (Data Sources)</h3>
                </div>
                <p className="text-gray-700 ml-16">
                  Connect to any database or data warehouse: PostgreSQL, MySQL, SQL Server, Oracle, 
                  Snowflake, BigQuery, Redshift, and more via standardized MCP protocol.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need for modern business intelligence
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-xl"
              >
                <feature.icon className="w-12 h-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Real-World Use Cases
            </h2>
            <p className="text-xl text-gray-600">
              See how executives get instant insights
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.role}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-full font-semibold">
                    {useCase.role}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Question:</div>
                    <div className="text-lg font-medium text-gray-900 italic">
                      {useCase.question}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Instant Result:</div>
                    <div className="text-gray-700 bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                      {useCase.result}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Enterprise-Ready Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built for security, scale, and seamless integration with your existing tools
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Security & Access Control */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-purple-600 p-3 rounded-lg">
                  <ShieldCheckIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Role-Based Access Control
                </h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                Granular security that respects your organizational hierarchy and data sensitivity requirements.
              </p>

              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">By Department</h4>
                  <p className="text-sm text-gray-600">Finance, Sales, HR, Operations - each with isolated access</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">By Role Level</h4>
                  <p className="text-sm text-gray-600">Executive, Manager, Analyst - permissions cascade intelligently</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Field-Level Security</h4>
                  <p className="text-sm text-gray-600">Sensitive data like salaries and PII automatically masked</p>
                </div>
              </div>
            </motion.div>

            {/* Workflow Automation */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <BoltIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Intelligent Automation
                </h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                Turn insights into action automatically. DB Talker doesn't just answer questions - it can act on them.
              </p>

              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Smart Alerts</h4>
                  <p className="text-sm text-gray-600">Automatic notifications when metrics hit thresholds</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Workflow Triggers</h4>
                  <p className="text-sm text-gray-600">Integration with Zapier, n8n, Slack, JIRA, and more</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Custom Actions</h4>
                  <p className="text-sm text-gray-600">Connect to your internal systems via webhooks</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Scalability */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl p-8"
          >
            <h3 className="text-3xl font-bold mb-6 text-center">
              Scales With Your Business
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">500+</div>
                <div className="text-lg font-semibold mb-2">Concurrent Users</div>
                <p className="text-gray-300 text-sm">Support hundreds of users querying simultaneously</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">1000s</div>
                <div className="text-lg font-semibold mb-2">Queries Per Day</div>
                <p className="text-gray-300 text-sm">Handle enterprise-scale query volumes effortlessly</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-400 mb-2">5 TB+</div>
                <div className="text-lg font-semibold mb-2">Data Warehouse</div>
                <p className="text-gray-300 text-sm">Optimized for medium to large data warehouses</p>
              </div>
            </div>

            <div className="mt-8 bg-white/10 rounded-lg p-6">
              <p className="text-center text-gray-200">
                <strong>Best Fit:</strong> Organizations with 100GB - 5TB data warehouses and 100-2000 database tables. 
                For very large data lakes (50TB+), we recommend a hybrid approach focusing on pre-aggregated insights.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose DB Talker?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg"
              >
                <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Transform Your Business Intelligence?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Stop waiting days for answers. Start conversations with your data today.
            </p>
            <div className="flex flex-col items-center">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/schedule-meeting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 text-lg shadow-xl"
                >
                  Try Live Demo
                </a>
                <a
                  href="#roi-calculator"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200 text-lg"
                >
                  Calculate Your ROI
                </a>
              </div>
              <p className="text-sm text-indigo-100 mt-4">
                Experience DB Talker's natural language queries and auto-generated charts
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DBTalkerPage;

